import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { navigate } from '@reach/router';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const styles = (theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
    // Let the billing view size naturally within the dashboard frame
    height: '150vh',
    // (no forced viewport height that can push content outside)
  },
  topRow: {
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    height: '100%',
  },
  metricLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    marginTop: theme.spacing(1),
    overflow: 'visible', // allow dropdowns to extend outside the card
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(2),
  },
});

const RANGE_OPTIONS = [
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 3 months' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last 12 months' },
];

// Match the dashboard Twilio color palette
const TWILIO_CHART_COLORS = [
  '#1b73e8',
  '#34a853',
  '#fbbc05',
  '#ea4335',
  '#a142f4',
  '#00acc1',
  '#fb8c00',
  '#0d9488',
  '#ef4444',
  '#6366f1',
  '#14b8a6',
  '#ff6d00',
];

const MessagingBilling = ({ classes }) => {
  const { t } = useTranslation();
  const [rangeDays, setRangeDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountOptions, setAccountOptions] = useState([
    { value: 'all', label: 'All accounts' },
  ]);
  const [locationOptions, setLocationOptions] = useState([
    { value: 'all', label: 'All locations' },
  ]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [activeMonthIndex, setActiveMonthIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchMonthlySpend() {
      setLoading(true);
      setError('');
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({
          rangeDays: String(rangeDays),
          timezone,
        });
        if (selectedAccount !== 'all') {
          params.append('accountIds', selectedAccount);
        }
        if (selectedLocation !== 'all') {
          params.append('boxIds', selectedLocation);
        }
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/monthly-spend?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization:
                'Bearer ' +
                JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken
                  .jwtToken,
            },
          }
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = await res.json();
        if (cancelled) return;
        const next = body && body.range ? body : null;
        setData(next);
        if (next && next.accounts) {
          const accounts = buildAccountOptions(next.accounts);
          setAccountOptions(accounts);
          if (!accounts.some((opt) => opt.value === selectedAccount)) {
            setSelectedAccount('all');
          }
        }
        if (next) {
          const locations = buildLocationOptions(next, selectedAccount);
          setLocationOptions(locations);
          if (!locations.some((opt) => opt.value === selectedLocation)) {
            setSelectedLocation('all');
          }
        }
        if (!next || !next.range) {
          setError('Unable to load monthly spend data.');
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Unable to load monthly spend data.');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchMonthlySpend();
    return () => {
      cancelled = true;
    };
  }, [rangeDays, selectedAccount, selectedLocation]);

  useEffect(() => {
    if (!data) {
      setLocationOptions([{ value: 'all', label: 'All locations' }]);
      setSelectedLocation('all');
      return;
    }
    const locations = buildLocationOptions(data, selectedAccount);
    setLocationOptions(locations);
    if (!locations.some((opt) => opt.value === selectedLocation)) {
      setSelectedLocation('all');
    }
  }, [data, selectedAccount, selectedLocation]);

  const chartPayload = useMemo(() => {
    if (!data) {
      return { categories: [], series: [] };
    }
    // Try to build location series first if locations exist
    const locations = ensureArray(data && data.locations);
    if (locations.length) {
      const locationSeries = buildLocationSeries(
        data,
        selectedAccount,
        selectedLocation
      );
      if (locationSeries.series.length) {
        return locationSeries;
      }
    }
    // Fall back to combined series
    const combinedMeta = buildCombinedCategories(data);
    return {
      categories: combinedMeta.map((entry) => entry.label),
      series: buildCombinedSeries(data, combinedMeta),
    };
  }, [data, selectedAccount, selectedLocation]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        background: 'transparent',
        toolbar: { show: true },
        animations: { enabled: true },
        events: {
          dataPointSelection: (event, chartContext, { dataPointIndex }) => {
            setActiveMonthIndex(dataPointIndex);
          },
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 6,
        },
      },
      colors: TWILIO_CHART_COLORS,
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff'],
      },
      xaxis: {
        categories: chartPayload.categories,
        labels: { rotate: -35, trim: false },
      },
      yaxis: {
        title: { text: 'Amount' },
        labels: {
          formatter: (value) =>
            formatCurrency(value, (data && data.currency) || 'USD'),
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      tooltip: {
        // Combined tooltip with required per-location data for the hovered month
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex, w }) =>
          buildTooltipAll(
            w && w.config && w.config.series,
            chartPayload.categories,
            (data && data.currency) || 'USD',
            dataPointIndex
          ),
      },
    }),
    [chartPayload, data && data.currency]
  );

  return (
    <div className={classes.container}>
      <Typography variant="h5" gutterBottom>
        Billing overview
      </Typography>

      <Card className={classes.chartCard} style={{ overflow: 'visible' }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Historical monthly spend
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Historical monthly spend includes the total for billed and all linked accounts.
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => navigate('/home/billing/invoices')}
            >
              View Invoices
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => navigate('/home/billing-analytics')}
            >
              {t('Billing.Analytics.ViewDetailed')}
            </Button>
          </div>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            {t('Billing.Historical.ClickToExpand')}
          </Typography>
          <div className={classes.filterRow}>
            <TextField
              select
              size="small"
              label="Date range"
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
              variant="outlined"
              style={{ minWidth: 160 }}
            >
              {RANGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <BillingPicker
              label="Account"
              value={selectedAccount}
              options={accountOptions}
              onChange={setSelectedAccount}
            />
            <BillingPicker
              label="Location"
              value={selectedLocation}
              options={locationOptions}
              onChange={setSelectedLocation}
            />
          </div>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : loading ? (
            <Typography variant="body2" color="textSecondary">
              Loading monthly spend…
            </Typography>
          ) : chartPayload.series.length && chartPayload.categories.length ? (
            <React.Fragment>
              <ReactApexChart
                options={chartOptions}
                series={chartPayload.series}
                type="bar"
                height={360}
              />
              <LocationDetailsPanel
                activeMonthIndex={activeMonthIndex}
                categories={chartPayload.categories}
                series={chartPayload.series}
                currency={(data && data.currency) || 'USD'}
                onClose={() => setActiveMonthIndex(null)}
              />
            </React.Fragment>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No monthly spend data for this selection.
            </Typography>
          )}
          {data && data.range && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8, textAlign: 'right' }}
            >
              {formatFootnote(
                data.range,
                (data && data.currency) || 'USD',
                chartPayload.categories.length
              )}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const LocationDetailsPanel = ({
  activeMonthIndex,
  categories,
  series,
  currency,
  onClose,
}) => {
  if (
    activeMonthIndex == null ||
    !categories ||
    !categories.length ||
    !Array.isArray(series) ||
    !series.length
  ) {
    return null;
  }

  const label = categories[activeMonthIndex] || '';

  const rows = series
    .map((entry, seriesIndex) => {
      const point =
        entry &&
        Array.isArray(entry.data) &&
        entry.data[activeMonthIndex];
      if (!point || Number(point.y) === 0) {
        return null;
      }
      const totals = (point.meta && point.meta.totals) || {};
      const sms = totals.sms || {};
      const mms = totals.mms || {};
      return {
        key: `${seriesIndex}-${activeMonthIndex}`,
        color: TWILIO_CHART_COLORS[seriesIndex % TWILIO_CHART_COLORS.length],
        name:
          (point.meta && point.meta.locationName) ||
          entry.name ||
          'Location',
        totalSpend: totals.totalSpend || point.y || 0,
        smsOut: (sms.outbound && sms.outbound.count) || 0,
        smsOutSeg: (sms.outbound && sms.outbound.segments) || 0,
        smsIn: (sms.inbound && sms.inbound.count) || 0,
        smsInSeg: (sms.inbound && sms.inbound.segments) || 0,
        mmsOut: (mms.outbound && mms.outbound.count) || 0,
        mmsOutSeg: (mms.outbound && mms.outbound.segments) || 0,
        mmsIn: (mms.inbound && mms.inbound.count) || 0,
        mmsInSeg: (mms.inbound && mms.inbound.segments) || 0,
      };
    })
    .filter(Boolean);

  if (!rows.length) {
    return null;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.4)',
          background:
            'linear-gradient(90deg, rgba(37,99,235,0.95), rgba(56,189,248,0.95))',
          color: '#ffffff',
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
        <span
          style={{
            fontSize: 11,
            color: 'rgba(241, 245, 249, 0.9)',
            marginLeft: 8,
          }}
        >
          Location breakdown
        </span>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              marginLeft: 'auto',
              color: '#fecaca', // light red
              '&:hover': {
                color: '#fee2e2',
                backgroundColor: 'rgba(248, 113, 113, 0.25)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        dividers
        style={{
          maxHeight: 420,
          background: 'linear-gradient(to bottom, #f9fafb, #ffffff)',
        }}
      >
        <div
          style={{
            maxHeight: 380,
            overflowY: 'auto',
          }}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              style={{
                marginBottom: 10,
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid rgba(209, 213, 219, 0.8)',
                background: '#ffffff',
                padding: '8px 10px',
                boxShadow:
                  '0 1px 1px rgba(15, 23, 42, 0.02), 0 1px 3px rgba(15, 23, 42, 0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: row.color,
                    marginRight: 6,
                  }}
                />
                <span>{row.name}</span>
                <span style={{ marginLeft: 'auto' }}>
                  {formatCurrency(row.totalSpend, currency)}
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#6b7280',
                  lineHeight: 1.5,
                  paddingLeft: 16,
                  marginTop: 4,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>SMS</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Inbound: {row.smsInSeg} segments</div>
                  <div>Outbound: {row.smsOutSeg} segments</div>
                </div>
                <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>MMS</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Inbound: {row.mmsInSeg} segments</div>
                  <div>Outbound: {row.mmsOutSeg} segments</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function buildLocationSeries(payload, accountId, locationId) {
  const locations = ensureArray(payload && payload.locations);
  if (!locations.length) {
    return { categories: [], series: [] };
  }
  
  // Filter locations by account and location if specified
  let filteredLocations = locations;
  if (accountId && accountId !== 'all') {
    filteredLocations = filteredLocations.filter(
      (loc) => String(loc && loc.accountId) === String(accountId)
    );
  }
  if (locationId && locationId !== 'all') {
    filteredLocations = filteredLocations.filter(
      (loc) => {
        const locId = loc && (loc.boxId || loc.id);
        return String(locId) === String(locationId);
      }
    );
  }
  
  if (!filteredLocations.length) {
    return { categories: [], series: [] };
  }
  
  // Collect all months across all locations
  const monthMeta = new Map();
  filteredLocations.forEach((location) => {
    ensureArray(location && location.months).forEach((entry) => {
      if (!entry) {
        return;
      }
      const key = entry.month || entry.label;
      if (!key) {
        return;
      }
      if (!monthMeta.has(key)) {
        monthMeta.set(key, {
          key: String(key),
          label: formatMonth(entry.month || key, entry.label),
        });
      }
    });
  });
  const months = Array.from(monthMeta.values()).sort((a, b) =>
    (a.key || '').localeCompare(b.key || '')
  );
  const categories = months.map((meta) => meta.label);

  const series = filteredLocations
    .map((location) => {
      const monthLookup = new Map();
      ensureArray(location && location.months).forEach((entry) => {
        if (!entry) {
          return;
        }
        const key = entry.month || entry.label;
        if (key) {
          monthLookup.set(String(key), entry);
        }
      });
      const name =
        (location && location.boxName) ||
        (location && location.name) ||
        (location && location.locationName) ||
        (location && location.boxId) ||
        (location && location.id) ||
        'Location';
      const data = months.map((meta) => {
        const entry =
          monthLookup.get(String(meta.key)) || monthLookup.get(String(meta.label));
        // Use entry directly since it has the new flat structure
        const totals = normalizeMonthlyTotals(entry || {});
        const spend = totals.totalSpend;
        return {
          x: meta.label,
          y: Number(spend) || 0,
          meta: {
            locationName: name,
            totals,
          },
        };
      });
      const hasSpend = data.some((point) => Number(point.y) > 0);
      return hasSpend
        ? {
            name,
            data,
          }
        : null;
    })
    .filter(Boolean);

  return { categories, series };
}

function buildAccountOptions(accounts) {
  const base = ensureArray(accounts).map((account) => {
    const raw =
      (account && (account.accountId || account.id || account.name)) || 'account';
    const label =
      (account && account.name) ||
      (account && account.displayName) ||
      (account && account.accountName) ||
      raw;
  return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All accounts' }, ...base];
}

function buildLocationOptions(payload, accountId) {
  let locations = ensureArray(payload && payload.locations);
  if (accountId && accountId !== 'all') {
    locations = locations.filter(
      (loc) => String(loc && loc.accountId) === String(accountId)
    );
  }
  const base = locations.map((location) => {
    const raw =
      (location && (location.boxId || location.id || location.boxName)) ||
      'location';
    const label =
      (location && location.boxName) ||
      (location && location.name) ||
      (location && location.locationName) ||
      raw;
    return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All locations' }, ...base];
}

function buildCombinedCategories(payload) {
  if (!payload) return [];
  const entries = Array.isArray(payload.combined) ? payload.combined : [];
  return entries
    .map((entry) => {
      const key = entry.month || entry.label;
      const label = entry.label || formatMonth(entry.month, entry.label);
      return key && label
        ? {
            key: String(key),
            label,
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.key || '').localeCompare(b.key || ''));
}

function buildCombinedSeries(payload, meta) {
  const entries = Array.isArray(payload && payload.combined)
    ? payload.combined
    : [];
  if (!entries.length || !meta.length) {
    return [];
  }
  const monthLookup = new Map();
  entries.forEach((entry) => {
    const key = entry.month || entry.label;
    if (key) {
      monthLookup.set(String(key), entry);
    }
    if (entry.label) {
      monthLookup.set(String(entry.label), entry);
    }
  });
  const data = meta.map((item) => {
    const entry =
      monthLookup.get(String(item.key)) || monthLookup.get(String(item.label));
    // Use entry directly since it has the new flat structure
    const totals = normalizeMonthlyTotals(entry || {});
    const spend = totals.totalSpend;
    return {
      x: item.label,
      y: Number(spend) || 0,
      meta: {
        locationName: 'All locations',
        totals,
      },
    };
  });
  const hasSpend = data.some((point) => Number(point.y) > 0);
  return hasSpend
    ? [
        {
          name: 'All locations',
          data,
        },
      ]
    : [];
}

function normalizeMonthlyTotals(source = {}) {
  // Handle new API structure with flat fields: inboundMessages, outboundMessages, etc.
  // Since API doesn't break down by SMS/MMS in monthly data, we'll put all messages under SMS
  const inboundMessages = Number(source.inboundMessages || 0);
  const inboundSegments = Number(source.inboundSegments || 0);
  const outboundMessages = Number(source.outboundMessages || 0);
  const outboundSegments = Number(source.outboundSegments || 0);
  const totalMessages = Number(source.totalMessages || inboundMessages + outboundMessages);
  const totalSegments = Number(source.totalSegments || inboundSegments + outboundSegments);
  
  // Calculate spend proportionally if we have breakdown, otherwise use totalSpend
  // Since we don't have SMS/MMS breakdown, we'll distribute totalSpend proportionally
  const totalSpend = Number(source.totalSpend || 0);
  
  // For backward compatibility, create nested structure
  // Put all messages under SMS since API doesn't provide SMS/MMS breakdown
  const sms = {
    count: totalMessages,
    segments: totalSegments,
    spend: totalSpend, // All spend goes to SMS since no breakdown
    inbound: {
      count: inboundMessages,
      segments: inboundSegments,
      spend: inboundMessages > 0 ? (totalSpend * inboundMessages / totalMessages) : 0,
    },
    outbound: {
      count: outboundMessages,
      segments: outboundSegments,
      spend: outboundMessages > 0 ? (totalSpend * outboundMessages / totalMessages) : 0,
    },
  };
  
  // MMS will be empty since API doesn't provide this breakdown
  const mms = {
    count: 0,
    segments: 0,
    spend: 0,
    inbound: { count: 0, segments: 0, spend: 0 },
    outbound: { count: 0, segments: 0, spend: 0 },
  };
  
  return {
    sms,
    mms,
    totalSpend: totalSpend,
  };
}

function ensureArray(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

const PICKER_PAGE_SIZE = 10;

const BillingPicker = ({ label, value, options = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const ref = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        value: String(option.value),
        label: option.label || String(option.value),
      })),
    [options]
  );

  const selected = useMemo(
    () =>
      normalizedOptions.find((option) => option.value === String(value)) ||
      normalizedOptions[0] || { label: 'Select', value: '' },
    [normalizedOptions, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return normalizedOptions;
    }
    const q = search.toLowerCase();
    return normalizedOptions.filter((option) =>
      option.label.toLowerCase().includes(q)
    );
  }, [normalizedOptions, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PICKER_PAGE_SIZE)
  );
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PICKER_PAGE_SIZE;
  const visibleOptions = filtered.slice(
    startIndex,
    startIndex + PICKER_PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [search, normalizedOptions]);

  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback(
    (nextValue) => {
      setOpen(false);
      onChange(nextValue);
    },
    [onChange]
  );

  const handlePageChange = (direction) => {
    setPage((prev) => {
      if (direction === 'prev') {
        return Math.max(1, prev - 1);
      }
      if (direction === 'next') {
        return Math.min(totalPages, prev + 1);
      }
      return prev;
    });
  };

  return (
    <div
      ref={ref}
      style={{ position: 'relative', minWidth: 220, maxWidth: 260 }}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          border: '1px solid #d1d5db',
          background: '#fff',
          borderRadius: 4,
          padding: '6px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.85rem',
          color: '#374151',
        }}
      >
        <span style={{ textAlign: 'left' }}>
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              color: '#6b7280',
              marginBottom: 2,
            }}
          >
            {label}
          </div>
          <div style={{ fontWeight: 600, color: '#111827' }}>
            {selected && selected.label ? selected.label : 'Select'}
          </div>
        </span>
        <span
          className={`icon-arrow-${open ? 'up' : 'down'}2`}
          style={{ marginLeft: 6 }}
        />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            width: 280,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
            zIndex: 2000,
            padding: 10,
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}...`}
            style={{
              width: '100%',
              border: '1px solid #d1d5db',
              borderRadius: 4,
              padding: '6px 8px',
              fontSize: '0.85rem',
              marginBottom: 8,
            }}
          />
          <div
            style={{
              maxHeight: 320, // taller dropdown list
              overflowY: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
            }}
          >
            {visibleOptions.length ? (
              visibleOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  role="button"
                  style={{
                    padding: '8px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.85rem',
                    background:
                      option.value === String(selected.value)
                        ? '#e0ebff'
                        : 'transparent',
                    color:
                      option.value === String(selected.value)
                        ? '#1d4ed8'
                        : '#111827',
                    fontWeight:
                      option.value === String(selected.value) ? 600 : 400,
                  }}
                >
                  <span>{option.label}</span>
                  {option.value === String(selected.value) && (
                    <span style={{ fontSize: '0.8rem', marginLeft: 8 }}>✔</span>
                  )}
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '12px 0',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                No results found.
              </div>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 8,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <div>
              Page {totalPages ? currentPage : 0} of {totalPages}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                style={{ marginRight: 4 }}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function formatCurrency(value, currency = 'USD') {
  const num = Number(value || 0);
  // Show exact decimal places from API response (up to 10 decimal places)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 10,
    useGrouping: true,
  }).format(num);
}

function formatMonth(month, fallback) {
  if (fallback) {
    return fallback;
  }
  if (!month) {
    return 'Unknown';
  }
  const parsed = moment(month, ['YYYY-MM', moment.ISO_8601], true);
  if (parsed.isValid()) {
    return parsed.format('MMMM YYYY');
  }
  const fallbackMoment = moment(month);
  return fallbackMoment.isValid() ? fallbackMoment.format('MMMM YYYY') : month;
}

function formatFootnote(range, currency, monthsCount) {
  const lines = [];
  lines.push(`${monthsCount} month${monthsCount === 1 ? '' : 's'} shown`);
  if (range && range.startDate && range.endDate) {
    const start = moment(range.startDate).format('MMM D, YYYY');
    const end = moment(range.endDate).format('MMM D, YYYY');
    lines.push(`Range: ${start} – ${end}`);
  }
  if (range && range.timezone) {
    lines.push(range.timezone);
  }
  if (currency) {
    lines.push(`Currency: ${currency.toUpperCase()}`);
  }
  return lines.join(' • ');
}

function buildTooltipAll(seriesConfig, categories, currency, index) {
  const label = categories[index] || '';
  if (!seriesConfig || !Array.isArray(seriesConfig)) {
    return '';
  }
  
  // Aggregate data across all locations
  let totalSpend = 0;
  let smsInCount = 0;
  let smsInSeg = 0;
  let smsInSpend = 0;
  let smsOutCount = 0;
  let smsOutSeg = 0;
  let smsOutSpend = 0;
  let mmsInCount = 0;
  let mmsInSeg = 0;
  let mmsInSpend = 0;
  let mmsOutCount = 0;
  let mmsOutSeg = 0;
  let mmsOutSpend = 0;
  
  seriesConfig.forEach((entry) => {
    const dataPoints = entry && entry.data;
    const point = dataPoints && dataPoints[index];
    if (!point || Number(point.y) === 0) {
      return;
    }
    const totals = (point.meta && point.meta.totals) || {};
    const sms = totals.sms || {};
    const mms = totals.mms || {};
    
    totalSpend += Number(totals.totalSpend || point.y || 0);
    smsInCount += Number((sms.inbound && sms.inbound.count) || 0);
    smsInSeg += Number((sms.inbound && sms.inbound.segments) || 0);
    smsInSpend += Number((sms.inbound && sms.inbound.spend) || 0);
    smsOutCount += Number((sms.outbound && sms.outbound.count) || 0);
    smsOutSeg += Number((sms.outbound && sms.outbound.segments) || 0);
    smsOutSpend += Number((sms.outbound && sms.outbound.spend) || 0);
    mmsInCount += Number((mms.inbound && mms.inbound.count) || 0);
    mmsInSeg += Number((mms.inbound && mms.inbound.segments) || 0);
    mmsInSpend += Number((mms.inbound && mms.inbound.spend) || 0);
    mmsOutCount += Number((mms.outbound && mms.outbound.count) || 0);
    mmsOutSeg += Number((mms.outbound && mms.outbound.segments) || 0);
    mmsOutSpend += Number((mms.outbound && mms.outbound.spend) || 0);
  });
  
  if (totalSpend === 0 && smsInSeg === 0 && smsOutSeg === 0 && mmsInSeg === 0 && mmsOutSeg === 0) {
    return `<div style="padding:16px;background:linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.12);border:1px solid rgba(226,232,240,0.8);">
      <div style="font-weight:700;font-size:14px;color:#1e293b;margin-bottom:8px;letter-spacing:0.3px;">${label}</div>
      <div style="font-size:12px;color:#94a3b8;padding:8px 12px;background:#f1f5f9;border-radius:6px;text-align:center;">No spend recorded</div>
    </div>`;
  }
  
  return `<div style="padding:0;background:#ffffff;border-radius:12px;box-shadow:0 8px 32px rgba(15,23,42,0.15), 0 2px 8px rgba(15,23,42,0.08);border:1px solid rgba(226,232,240,0.8);overflow:hidden;min-width:280px;">
    <div style="background:linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);padding:14px 16px;color:#ffffff;">
      <div style="font-weight:700;font-size:13px;letter-spacing:0.5px;text-transform:uppercase;opacity:0.9;margin-bottom:4px;">${label}</div>
      <div style="font-weight:700;font-size:20px;display:flex;align-items:center;gap:8px;">
        <span style="font-size:14px;opacity:0.9;">All Locations:</span>
        <span style="font-size:20px;">${formatCurrency(totalSpend, currency)}</span>
      </div>
    </div>
    <div style="padding:16px;background:#f8fafc;">
      <div style="background:#ffffff;border-radius:10px;padding:12px;margin-bottom:12px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="width:4px;height:20px;background:linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%);border-radius:2px;"></div>
          <div style="font-weight:700;font-size:13px;color:#0f172a;letter-spacing:0.3px;">SMS</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style="background:#f0f9ff;border-radius:8px;padding:10px;border-left:3px solid #0ea5e9;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Inbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${smsInCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${smsInSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(smsInSpend, currency)}</span></div>
          </div>
          <div style="background:#fef3c7;border-radius:8px;padding:10px;border-left:3px solid #f59e0b;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Outbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${smsOutCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${smsOutSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(smsOutSpend, currency)}</span></div>
          </div>
        </div>
      </div>
      <div style="background:#ffffff;border-radius:10px;padding:12px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="width:4px;height:20px;background:linear-gradient(180deg, #ec4899 0%, #db2777 100%);border-radius:2px;"></div>
          <div style="font-weight:700;font-size:13px;color:#0f172a;letter-spacing:0.3px;">MMS</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style="background:#fdf2f8;border-radius:8px;padding:10px;border-left:3px solid #ec4899;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Inbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${mmsInCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${mmsInSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(mmsInSpend, currency)}</span></div>
          </div>
          <div style="background:#fce7f3;border-radius:8px;padding:10px;border-left:3px solid #f472b6;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Outbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${mmsOutCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${mmsOutSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(mmsOutSpend, currency)}</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>`;
}

export default withStyles(styles)(MessagingBilling);






// WEBPACK FOOTER //
// ./src/components/billing/MessagingBilling.js